import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

const SUPPORTED_INPUT_FORMATS = ['.mp4', '.mov', '.avi', '.webm', '.mkv', '.wmv', '.m4v', '.3gp'];

export function isSupported(file: File): boolean {
  const ext = '.' + file.name.split('.').pop()?.toLowerCase();
  return SUPPORTED_INPUT_FORMATS.includes(ext);
}

let ffmpegInstance: FFmpeg | null = null;
let ffmpegLoaded = false;

export async function convertToMp4(
  file: File,
  onProgress?: (progress: number) => void,
): Promise<File> {
  if (!ffmpegInstance) {
    ffmpegInstance = new FFmpeg();
  }

  if (!ffmpegLoaded) {
    onProgress?.(0);
    // Load FFmpeg core from CDN
    // Single-thread core — no COOP/COEP headers needed, works in all browsers including Safari
    // Note: HEVC decode handled by -c:v libx264 transcoding (input decode is separate from output encode)
    const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
    await ffmpegInstance.load({
      coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
    });
    ffmpegLoaded = true;
  }

  // Remove any previous progress listener before adding a new one
  const prevHandler = (ffmpegInstance as unknown as { __progressHandler?: (p: { progress: number }) => void }).__progressHandler;
  if (prevHandler) ffmpegInstance.off('progress', prevHandler);
  const progressHandler = ({ progress }: { progress: number }) => {
    onProgress?.(Math.min(99, Math.round(progress * 100)));
  };
  (ffmpegInstance as unknown as { __progressHandler?: (p: { progress: number }) => void }).__progressHandler = progressHandler;
  ffmpegInstance.on('progress', progressHandler);

  const inputName = 'input.' + file.name.split('.').pop()?.toLowerCase();
  const outputName = file.name.replace(/\.[^.]+$/, '') + '_converted.mp4';

  await ffmpegInstance.writeFile(inputName, await fetchFile(file));
  // Convert to H.264 MP4 — handles HEVC/H.265 (iPhone), H.264, VP9, etc.
  const exitCode = await ffmpegInstance.exec([
    '-i', inputName,
    '-c:v', 'libx264',         // Transcode to H.264
    '-c:a', 'aac',             // AAC audio
    '-preset', 'fast',
    '-crf', '23',              // Good quality
    '-movflags', '+faststart',
    outputName,
  ]);
  if (exitCode !== 0) {
    throw new Error('FFmpeg conversion failed (exit code ' + exitCode + '). The video codec may not be supported in your browser.');
  }

  const data = await ffmpegInstance.readFile(outputName);
  // FileData = Uint8Array | string. We always get Uint8Array for binary files.
  // Copy bytes into a guaranteed plain ArrayBuffer to satisfy TypeScript's Blob types.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rawBytes = data as any as Uint8Array;
  const plainBuffer = new ArrayBuffer(rawBytes.byteLength);
  new Uint8Array(plainBuffer).set(rawBytes);
  const blob = new Blob([plainBuffer], { type: 'video/mp4' });
  return new File([blob], outputName, { type: 'video/mp4' });
}
